// Screen 5 · Fast-capture — the type-it path (core of Module D).
// Frame + the three hard cells (friendly prompts, NO red asterisks) + body.
// Save draft or Submit → render the 5-stage plan. No quality meter, no scoring.
//
// The Week box drives the lesson (owner ruling A3, 21 Oct 2026): the form opens on the school week the teacher
// came from (or this school week) and on THAT week's lesson in the year plan; changing the Week changes the
// lesson, its topic and the materials panel beside it. Teaching a different week's lesson (catch-up) is one
// labelled option, and the page says so in words ("School week 5 · teaching Week 3's lesson"). "This week" is the
// school calendar's week for everyone (A2) — a class's plans no longer move it; they only show how far they reach.
// The prefill is a head start, never a gate: no map → the form is exactly the old form; a far jump asks for ONE
// explanatory line (warn, never block).
//
// No phantom drafts: opening the form, the prefilled topic, and choosing a class, week or lesson create nothing.
// A draft exists only once the teacher has WRITTEN something (a writing box, the topic, an own resource) or pressed
// Save draft / Submit / Make a worksheet. Once one exists, "Discard this draft" removes it.
//
// Autosave: a phone form must never lose 8 minutes of typing. Once the teacher has written, the frame
// is auto-created ONCE (in-flight latch — never duplicates) and the cells upsert on a quiet
// 2s debounce; Save draft / Submit reuse the same lesson. An abandoned autosaved draft is a
// normal Draft card on This Week — visible, resumable, deletable, never silent litter.

import { api } from "../api";
import { autoGrow, esc } from "../dom";
import { navigate, registerNavigationGuard } from "../router";
import { appendSourceLines, planningWeekHref } from "../planning-source";
import { initAutosave } from "../autosave";
import { t } from "../i18n";
import { STEPS, serializeSteps, firstShortfall, typedLength, STEP_FLOOR, CELL_FLOOR, type StepKey } from "../plan-steps";
import { icon } from "../icons";
import { pageHeader, rangeLabel, toast } from "../ui";
import { splitPeriods, type PeriodSplit, type Strand } from "../materials";
import { loadLessonRefs, teacherTitle } from "../lesson-refs";
import { workspaceHtml, mountWorkspace, type WorkspaceHandle } from "../plan-workspace";
import { mountRichText, type RichTextHandle } from "../rich-text";
import { resourcesCardHtml, mountResources, type ResourcesHandle } from "../plan-resources-ui";
import { termWeekRange } from "../term-weeks";
import { plansReachLine, wireDraftDelete } from "../lesson-data";
import {
  loadGrade,
  gradeFromClass,
  termNumber,
  classPosition,
  schoolWeekNode,
  type ClassPosition,
  type CurriculumNode,
  type GradeCurriculum,
  type NodePlanState,
} from "../curriculum";
import { rememberClass, selectedClassId } from "./roadmap";
import type { WeekClock } from "../clock";

interface Cls {
  id: string;
  name: string;
  level: string | null;
  periods_per_week: number;
}
interface TermInfo {
  term: { id: string; name: string; start_date: string; end_date: string };
  weeks: number[];
  current_week: number;
  is_current?: boolean;
}

interface MapContext {
  cur: GradeCurriculum;
  /** The class's planned weeks — only for the "Your plans reach …" line, never for which week it is. */
  pos: ClassPosition;
  /** WEB-P3: this class's actual period split (class.periods_per_week, 0024) — never the grade
   *  file's own `rhythm` constant (owner ruling 2026-09-05). */
  split: PeriodSplit;
  /** WEB-P3: "prefilled from … the teacher's last plan" — the most recent lesson for this class
   *  that had a slot, advanced by one ordinal (wrapping within the same strand's count). Null when
   *  no prior lesson carried a slot yet (a brand-new class, or a class planned before WEB-P3). */
  lastSlot: { strand: Strand; ordinal: number } | null;
}

/** The class's map (null when the class has no curriculum — the old flow). */
async function loadMapContext(cls: Cls): Promise<MapContext | null> {
  const grade = gradeFromClass(cls);
  const cur = grade ? await loadGrade(grade) : null;
  if (!cur) return null;
  let states: NodePlanState[] = [];
  try {
    const res = await api<{ nodes: NodePlanState[] }>(
      `/api/classes/${encodeURIComponent(cls.id)}/position`,
    );
    states = res.nodes;
  } catch {
    /* position is a nicety — the form works from the school week alone */
  }
  const pos = classPosition(cur, states);

  const split = splitPeriods(cls.periods_per_week);
  let lastSlot: MapContext["lastSlot"] = null;
  try {
    const { lessons } = await api<{ lessons: { strand: string | null; slot: number | null }[] }>(
      `/api/classes/${encodeURIComponent(cls.id)}/lessons`,
    );
    const last = lessons.find((l) => l.strand && l.slot);
    if (last?.strand && last.slot) {
      const strand = last.strand as Strand;
      const count = Math.max(split[strand], 1);
      // Advance one ordinal within the same strand, wrapping — the closest thing to "next period
      // in rotation" without inventing a whole-week day-by-day timetable this app doesn't model.
      lastSlot = { strand, ordinal: (last.slot % count) + 1 };
    }
  } catch {
    /* last-plan lookup is a nicety — the picker still works, just defaults to period 1 */
  }

  return { cur, pos, split, lastSlot };
}

export async function renderPlanNew(
  app: HTMLElement,
  _params: Record<string, string>,
  query: URLSearchParams,
): Promise<void> {
  // scope=own → only the session user's classes (a Lead plans against their OWN classes too).
  const [{ classes }, currentTerm] = await Promise.all([
    api<{ classes: Cls[] }>("/api/classes?scope=own"),
    api<TermInfo>("/api/terms/current"),
    loadLessonRefs(), // the week picker's titles name the unit
  ]);

  if (classes.length === 0) {
    app.innerHTML = `
      ${pageHeader({ title: t("plan.title"), crumbs: [{ label: "Home", href: "/" }, { label: t("plan.title") }] })}
      <div class="empty-state"><p>${t("plan.no_class")}</p><a class="btn" data-link href="/classes">${t("plan.add_class")}</a></div>`;
    return;
  }

  // Week's CTA carries ?class=…, so the form opens on the class you were just looking at.
  const preselect = query.get("class") ?? selectedClassId();
  // JOB-W02: ?node=… opens the form on a NAMED lesson of the year plan, so "start a session for this lesson
  // of this class" is one link. Without ?week= the form opens on that lesson's own school week. An unknown id is
  // ignored, never an error.
  const preselectNode = query.get("node");
  const startClass = classes.find((c) => c.id === preselect) ?? classes[0]!;
  let term = currentTerm;
  if (query.get("term")) {
    const week = query.get("week") ?? "1";
    const selected = await api<{ term: { id: string; name: string; start_date: string; end_date: string; week_count: number }; week_no: number }>(
      `/api/classes/${encodeURIComponent(startClass.id)}/weekly-preparation?term_id=${encodeURIComponent(query.get("term")!)}&week_no=${encodeURIComponent(week)}`,
    );
    term = { term: selected.term, weeks: Array.from({ length: selected.term.week_count }, (_, i) => i + 1), current_week: selected.week_no, is_current: selected.term.id === currentTerm.term.id && currentTerm.is_current !== false };
  } else if (query.has("week") && term.weeks.includes(Number(query.get("week")))) {
    term = { ...term, current_week: Number(query.get("week")) };
  } else if (preselectNode) {
    // A lesson link with no school week: open on that lesson's own week when it falls in this term.
    const startGrade = gradeFromClass(startClass);
    const startCur = startGrade ? await loadGrade(startGrade) : null;
    const wanted = startCur?.nodes.find((n) => n.id === preselectNode) ?? null;
    if (wanted && wanted.term === termNumber(term.term) && term.weeks.includes(wanted.suggested_week)) {
      term = { ...term, current_week: wanted.suggested_week };
    }
  }
  const termN = termNumber(term.term);

  const classOpts = classes
    .map(
      (c) =>
        `<option value="${esc(c.id)}"${c.id === startClass.id ? " selected" : ""}>${esc(c.name)}${
          c.level ? ` · ${esc(c.level)}` : ""
        }</option>`,
    )
    .join("");
  const weekOpts = term.weeks
    .map((w) => `<option value="${w}"${w === term.current_week ? " selected" : ""}>${t("common.week_n", { n: w })}</option>`)
    .join("");

  // The planning workspace (teacher review, 15 Oct 2026): the plan and this week's materials side by side, on one
  // screen, so a teacher never leaves the plan to look at the book, the worksheet or the Teacher Pack. The form is
  // the same form as before (same fields, same paste lock, same autosave); only the frame around it changed.
  const need = (id: string, n: number): string => `<span class="need" data-need="${id}" data-floor="${n}" aria-live="polite"></span>`;
  const form = `
        <section class="card form-card pw-frame">
          <h2 class="card-title">${esc(term.term.name)} · My lesson</h2>
          <div class="form-grid">
            <label for="class">${t("plan.class")}<select id="class">${classOpts}</select></label>
            <label for="week">${t("plan.week")}<select id="week">${weekOpts}</select></label>
          </div>
          <p class="hint" id="weekdates"></p>
          <div id="mapcard"></div>
          <div class="form-grid">
            <label for="topic" class="span-2">${t("plan.topic")}<input id="topic" placeholder="${t("plan.topic_placeholder")}" required /></label>
            <label for="duration">${t("plan.duration")}<input id="duration" type="number" value="60" min="5" step="5" /></label>
          </div>
        </section>
        <section class="card form-card" data-pw-source><p>Loading this week's syllabus…</p></section>
        <section class="card form-card">
          <label for="materials">Objectives / Materials</label>
          <p class="hint">Use the sourced starting points above, then add your objectives and materials. Your teaching decisions stay in the boxes below.</p>
          <textarea id="materials" class="grow"></textarea>
        </section>
        ${resourcesCardHtml()}
        <section class="card form-card">
          <h2 class="card-title">${t("plan.thinking")}</h2>
          <p class="hint">${t("plan.thinking_hint")}</p>
          <label for="prior">${t("plan.prior")} ${need("prior", CELL_FLOOR.prior)}</label>
          <p class="hint">${t("plan.prior_hint")}</p>
          <textarea id="prior" class="grow"></textarea>

          <label for="misconception">${t("plan.misconception")} ${need("misconception", CELL_FLOOR.misconception)}</label>
          <p class="hint">${t("plan.misconception_hint")}</p>
          <textarea id="misconception" class="grow"></textarea>

          <label for="cfu">${t("plan.cfu")} ${need("cfu", CELL_FLOOR.check_for_understanding)}</label>
          <p class="hint">${t("plan.cfu_hint")}</p>
          <textarea id="cfu" class="grow"></textarea>
        </section>

        <section class="card form-card">
          <h2 class="card-title">${t("plan.the_plan")}</h2>
          <div id="clockbanner"></div>
          <h3 class="sub-title">${t("plan.procedure")}</h3>
          <p class="hint">${t("plan.procedure_hint")} Use the buttons above each box for points, numbers and bold.</p>
          ${STEPS.map(
            (s) => `<label for="step-${s.key}" class="step-label"><span class="step-n">${s.label.slice(0, 1)}</span>${s.label.slice(2)} ${need(`step-${s.key}`, STEP_FLOOR[s.key])}</label>
          <p class="hint">${s.hint}</p>
          <textarea id="step-${s.key}" class="step-box grow"></textarea>
          <div class="step-extras"><span class="step-chips" data-step-chips="${s.key}" hidden></span><button type="button" class="linkbtn small" data-add-activity="${s.key}">${icon("plus", "ico-sm")} Add your own activity to this step</button></div>`,
          ).join("")}
        </section>`;
  app.innerHTML = `
    ${pageHeader({
      crumbs: [{ label: "Home", href: "/" }, { label: t("plan.title") }],
      title: t("plan.title"),
      sub: "Start from the syllabus, add materials, then describe how your class will learn. Drafts save as you write.",
      actions: `<span class="sync-chip"><span class="dot"></span><span id="autosave" aria-live="polite"></span></span>`,
    })}
    <p><button type="button" class="btn secondary" id="back-week">Back to this week</button></p>
    ${workspaceHtml(form)}
    <div class="action-bar">
      <div id="err" class="error" hidden></div>
      <span class="ab-progress" id="ab-progress"></span>
      <button class="btn secondary sm" id="discard" type="button" style="display:none">Discard this draft</button>
      <button class="btn secondary" id="draft" type="button">${t("plan.save_draft")}</button>
      <button class="btn" id="submit" type="button">${icon("check")}<span>${t("plan.submit")}</span></button>
    </div>`;

  // Formatting in every writing box (teacher review, point 5): bold, italic, points, numbers, sub-points. The editor
  // keeps the same text column (a light markdown) and the same paste lock: the thinking cells and every procedure
  // step stay the teacher's own English (spec §2, §4.4); Materials, topic, class and week stay pasteable.
  const rich = new Map<string, RichTextHandle>();
  for (const id of ["prior", "misconception", "cfu", ...STEPS.map((s) => `step-${s.key}`)]) {
    const box = app.querySelector<HTMLTextAreaElement>(`#${id}`);
    if (box) rich.set(id, mountRichText(box, { pasteLocked: true }));
  }
  const matBox = app.querySelector<HTMLTextAreaElement>("#materials");
  if (matBox) rich.set("materials", mountRichText(matBox, { pasteLocked: false, placeholder: "Aims, books and pages, cards, audio… (you can paste a list here)" }));
  /** Put the caret in a field (its editor when it has one — the textarea underneath is hidden). */
  const focusField = (id: string): void => {
    const r = rich.get(id);
    const el = r ? r.editor : app.querySelector<HTMLElement>(`#${id}`);
    if (r) r.focus(); else el?.focus();
    el?.scrollIntoView({ block: "center" });
  };
  // The materials panel and the plan's "Your own activities, links and files" card, which also lists the book pages
  // added from the Book tab (both created once autosave exists, below).
  let ws: WorkspaceHandle | null = null;
  let resources: ResourcesHandle | null = null;

  // Auto-grow: a box grows with the teacher's writing instead of hiding it behind a scrollbar
  // (wired here; the router sizes every .grow box again once the page is on screen).
  autoGrow(app);

  // What Submit still needs: a quiet tick beside each box once it has enough of the teacher's own
  // words (the same floor Submit checks), and a count in the action bar. Drafts are always free.
  const needFields: { id: string; box: string; floor: number }[] = [
    { id: "prior", box: "#prior", floor: CELL_FLOOR.prior },
    { id: "misconception", box: "#misconception", floor: CELL_FLOOR.misconception },
    { id: "cfu", box: "#cfu", floor: CELL_FLOOR.check_for_understanding },
    ...STEPS.map((st) => ({ id: `step-${st.key}`, box: `#step-${st.key}`, floor: STEP_FLOOR[st.key] })),
  ];
  function refreshNeeds(): void {
    let done = 0;
    for (const f of needFields) {
      const el = app.querySelector<HTMLTextAreaElement>(f.box);
      const tag = app.querySelector<HTMLElement>(`[data-need="${f.id}"]`);
      const ok = !!el && typedLength(el.value) >= f.floor;
      if (ok) done++;
      if (tag) {
        tag.textContent = ok ? "✓ enough" : el && el.value.trim() ? "keep going" : "";
        tag.classList.toggle("ok", ok);
      }
    }
    const bar = app.querySelector<HTMLElement>("#ab-progress");
    if (bar) bar.textContent = done === needFields.length ? "Ready to submit" : `${done} of ${needFields.length} parts written`;
  }
  app.querySelectorAll("textarea").forEach((el) => el.addEventListener("input", refreshNeeds));
  refreshNeeds();

  const val = (id: string): string => {
    const el = app.querySelector(id) as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null;
    return el ? el.value : "";
  };
  const err = app.querySelector<HTMLElement>("#err")!;
  const discard = app.querySelector<HTMLButtonElement>("#discard")!;
  let discardWired = false;

  // ── the school week and its lesson ──
  let map: MapContext | null = null;
  let lastPrefill = ""; // the topic text WE wrote — only ever replace our own words
  let firstRender = true; // ?node= opens the form once; a later class switch follows the Week box again

  const nodeById = (id: string): CurriculumNode | null =>
    map?.cur.nodes.find((n) => n.id === id) ?? null;

  function chosenNode(): CurriculumNode | null {
    const id = val("#node");
    return id ? nodeById(id) : null;
  }

  /** The year plan's lesson for the school week in the Week box: exact, else the nearest earlier one. */
  function weekNode(): { node: CurriculumNode | null; exact: boolean } {
    if (!map || !termN) return { node: null, exact: false };
    return schoolWeekNode(map.cur, termN, Number(val("#week")));
  }

  function jumpDistance(): number {
    const chosen = chosenNode();
    const own = weekNode().node;
    if (!chosen || !own) return 0;
    return Math.abs(chosen.seq - own.seq);
  }

  function prefillTopic(node: CurriculumNode | null): void {
    const topic = app.querySelector("#topic") as HTMLInputElement | null;
    if (!topic || !node) return;
    if (topic.value.trim() === "" || topic.value === lastPrefill) {
      // the teacher-facing title ("Unit 1 · How Much Food? …", "Blueprint 4 overview …"), not the
      // year plan's shorthand ("U1 …", "BP4 …") — this is the name the plan carries everywhere after
      const title = teacherTitle(node);
      topic.value = title;
      lastPrefill = title;
    }
  }

  /** The Week box's dates, and the panel beside the form, in one voice: "Week 5 · 1 Dec – 7 Dec · <lesson>". */
  function weekRange(): string {
    const w = Number(val("#week"));
    if (!w) return "";
    const r = termWeekRange({ start_date: term.term.start_date, end_date: term.term.end_date }, w);
    return rangeLabel(r.start, r.end);
  }

  /** Say, in words, when the lesson is not the school week's own: a catch-up pick, or a week the year plan has no
   *  step for. Also names the panel for the week the picker shows (owner ruling A3). */
  function refreshStepNote(): void {
    const w = Number(val("#week"));
    const chosen = chosenNode();
    const own = weekNode();
    const dates = weekRange();
    const datesEl = app.querySelector<HTMLElement>("#weekdates");
    if (datesEl) datesEl.textContent = dates ? `${t("common.week_n", { n: w })} runs ${dates}.` : "";
    const note = app.querySelector<HTMLElement>("#stepnote");
    const sameWeek = !!chosen && chosen.term === termN && chosen.suggested_week === w;
    let text = "";
    if (chosen && !sameWeek) {
      const where = chosen.term === termN ? `Week ${chosen.suggested_week}` : `Term ${chosen.term} Week ${chosen.suggested_week}`;
      text = own.node && !own.exact && chosen.id === own.node.id
        ? `The year plan has no new lesson for school week ${w}, so this is ${where}'s lesson — catch up or review.`
        : `School week ${w} · teaching ${where}'s lesson (catch-up).`;
    } else if (!chosen && map) {
      text = "This plan is not linked to a lesson of the year plan.";
    }
    if (note) {
      note.textContent = text;
      note.hidden = !text;
    }
    // the panel's heading names the SAME week as the picker (the lesson's own week only when it differs)
    if (chosen) {
      const lesson = teacherTitle(chosen);
      ws?.setHeading(sameWeek
        ? `Week ${w}${dates ? ` · ${dates}` : ""} · ${lesson}`
        : `School week ${w}${dates ? ` · ${dates}` : ""} · teaching ${chosen.term === termN ? "" : `Term ${chosen.term} `}Week ${chosen.suggested_week}'s lesson · ${lesson}`);
    } else {
      ws?.setHeading(null);
    }
  }

  /** The materials panel follows the chosen lesson (it shows that week's book pages, worksheet and Teacher Pack —
   *  never a prefill of the thinking cells or the procedure: Module D rails, the teacher types). */
  function refreshPackRow(): void {
    const chosen = chosenNode();
    ws?.setNode(chosen?.id ?? null);
    refreshStepNote();
  }

  /** Show/hide the one-line note + review hint for the current pick (plain words, no blocking). */
  function refreshNoteRow(): void {
    const row = app.querySelector<HTMLElement>("#noterow");
    const review = app.querySelector<HTMLElement>("#reviewhint");
    if (!row) return;
    const chosen = chosenNode();
    row.hidden = !(chosen && jumpDistance() > 3);
    if (review) review.hidden = !(chosen && chosen.type === "review" && jumpDistance() > 0);
  }

  /** The week's own lesson carries a tick in the catch-up list, so the teacher can see which one it is. */
  function refreshOptionMarks(): void {
    const own = weekNode().node;
    app.querySelectorAll<HTMLOptionElement>("#node option[data-title]").forEach((o) => {
      o.textContent = `${o.dataset.when} — ${o.dataset.title}${own && o.value === own.id ? " ✓" : ""}`;
    });
  }

  /** The Week box changed: the lesson follows it (exact; the nearest earlier one, with a note, when the year plan has
   *  none), then the topic we prefilled, the review hint and the materials panel follow the lesson. */
  function followWeek(): void {
    const sel = app.querySelector("#node") as HTMLSelectElement | null;
    const own = weekNode().node;
    if (sel && own) sel.value = own.id;
    prefillTopic(chosenNode());
    refreshOptionMarks();
    refreshNoteRow();
    refreshPackRow();
  }

  function renderMapCard(): void {
    const holder = app.querySelector<HTMLElement>("#mapcard")!;
    if (!map) {
      holder.innerHTML = "";
      ws?.setNode(null);
      ws?.setHeading(null);
      return;
    }
    const { cur } = map;
    const own = weekNode().node;
    const opened = firstRender && preselectNode && cur.nodes.some((n) => n.id === preselectNode) ? preselectNode : own?.id ?? "";
    firstRender = false;
    const opts = [1, 2, 3]
      .map((tn) => {
        const rows = cur.nodes
          .filter((n) => n.term === tn)
          .map(
            (n) =>
              `<option value="${esc(n.id)}"${n.id === opened ? " selected" : ""} data-when="Term ${n.term} · Week ${n.suggested_week}" data-title="${esc(teacherTitle(n))}">` +
              `Term ${n.term} · Week ${n.suggested_week} — ${esc(teacherTitle(n))}</option>`,
          )
          .join("");
        return `<optgroup label="${t("roadmap.term_n", { n: tn })}">${rows}</optgroup>`;
      })
      .join("");
    const reach = plansReachLine(map.pos);
    holder.innerHTML = `
      <div class="nodecard mapcard">
        <p class="hint" id="stepnote" hidden></p>
        ${reach ? `<p class="hint" id="reachline">${esc(reach)}</p>` : ""}
        <details class="pw-more">
        <summary>Teach a different week's lesson (catch-up)</summary>
        <label for="node">Which week's lesson do you want to teach?</label>
        <p class="hint">The school week stays the same. Nearby lessons are free to pick; jumping further asks for one line, so your Lead knows why. Changing the lesson keeps your writing.</p>
        <select id="node">
          <option value="">${t("plan.map_unlink")}</option>
          ${opts}
        </select>
        </details>
        <p class="hint" id="reviewhint" hidden>${t("plan.map_review_note")}</p>

        <div id="noterow" hidden>
          <label for="node_note">${t("plan.map_note_label")}</label>
          <input id="node_note" maxlength="200" placeholder="${t("plan.map_note_placeholder")}" />
        </div>

        <details class="pw-more">
          <summary>More (optional): which part of the week this lesson is</summary>
          <label for="strand">${t("plan.strand")}</label>
          <select id="strand">
            <option value="">${t("plan.strand_skip")}</option>
            <option value="coursebook">${t("plan.strand_coursebook")}</option>
            <option value="thread">${t("plan.strand_thread")}</option>
            <option value="fluency">${t("plan.strand_fluency")}</option>
          </select>

          <div id="slotrow" hidden>
            <label for="slot">${t("slot.period")}</label>
            <select id="slot"></select>
          </div>
        </details>
      </div>`;
    prefillTopic(nodeById(opened) ?? own);
    refreshOptionMarks();
    refreshNoteRow();
    refreshSlotOptions();
    refreshPackRow();
    const nodeSel = holder.querySelector("#node") as HTMLSelectElement | null;
    nodeSel?.addEventListener("change", () => {
      // a catch-up pick: the lesson changes, the school week does not
      prefillTopic(chosenNode());
      refreshNoteRow();
      refreshPackRow();
      if (lessonId) autosave.schedule();
    });
    const strandSel = holder.querySelector("#strand") as HTMLSelectElement | null;
    strandSel?.addEventListener("change", () => {
      refreshSlotOptions();
      if (lessonId) autosave.schedule();
    });
    holder.querySelector("#slot")?.addEventListener("change", () => { if (lessonId) autosave.schedule(); });
    // The one-line "why" is the teacher's own writing; it counts as having written something.
    holder.querySelector("#node_note")?.addEventListener("input", () => { touched = true; autosave.schedule(); });
  }

  /** The period-ordinal picker (WEB-P3): only meaningful once a strand is picked (a period number
   *  with no strand isn't a real slot — the API rejects it too, see node-link.ts). Options are the
   *  class's own periods_per_week split; a lastSlot in the SAME strand prefills the next ordinal
   *  ("prefilled from … the teacher's last plan"), otherwise period 1. */
  function refreshSlotOptions(): void {
    const holder = app.querySelector<HTMLElement>("#mapcard")!;
    const row = holder.querySelector<HTMLElement>("#slotrow");
    const strandSel = holder.querySelector("#strand") as HTMLSelectElement | null;
    const slotSel = holder.querySelector("#slot") as HTMLSelectElement | null;
    if (!row || !strandSel || !slotSel || !map) return;
    const strand = (strandSel.value || null) as Strand | null;
    if (!strand) {
      row.hidden = true;
      slotSel.innerHTML = "";
      return;
    }
    row.hidden = false;
    const count = Math.max(map.split[strand], 1);
    const preferred = map.lastSlot && map.lastSlot.strand === strand ? map.lastSlot.ordinal : 1;
    slotSel.innerHTML = Array.from({ length: count }, (_, i) => i + 1)
      .map((n) => `<option value="${n}"${n === preferred ? " selected" : ""}>${n}</option>`)
      .join("");
  }

  // The clock banner (P1b Job 3) — a one-line note above the step picker when this grade's week
  // isn't plain (a lost day, or both). Decorative: a failed lookup just omits it.
  function renderClockBanner(clock: WeekClock | null): void {
    const holder = app.querySelector<HTMLElement>("#clockbanner");
    if (!holder) return;
    if (!clock || clock.teaching_days >= 5) {
      holder.innerHTML = "";
      return;
    }
    const names = clock.lost_days.map((d) => esc(d.label)).join(", ");
    holder.innerHTML = `<p class="hint">${clock.teaching_days} teaching day${clock.teaching_days === 1 ? "" : "s"} this week${names ? ` — ${names}` : ""}.</p>`;
  }

  async function switchClass(id: string): Promise<void> {
    const cls = classes.find((c) => c.id === id);
    if (!cls) return;
    rememberClass(cls.id);
    map = await loadMapContext(cls);
    renderMapCard();
    const grade = gradeFromClass(cls);
    const clock = grade ? await api<WeekClock | { clock: null }>(`/api/clock?grade=${grade}`).catch(() => null) : null;
    renderClockBanner(clock && "week_no" in clock ? clock : null);
  }

  const frame = () => ({
    class_id: val("#class"),
    week_no: Number(val("#week")),
    topic: val("#topic").trim(),
    duration_min: Number(val("#duration")) || 60,
    node_id: val("#node") || null,
    strand: val("#strand") || null,
    node_note: val("#node_note").trim() || null,
    // WEB-P3: only meaningful with a strand (the API rejects one without the other) — val() reads
    // "" when #slotrow is hidden (no strand chosen), which becomes null exactly like the others.
    slot: val("#strand") && val("#slot") ? Number(val("#slot")) : null,
  });
  // The five named steps serialize into the single `procedure` column (see src/plan-steps.ts).
  const stepTexts = () =>
    Object.fromEntries(STEPS.map((s) => [s.key, val(`#step-${s.key}`)])) as Record<StepKey, string>;
  const planBody = () => ({
    prior: val("#prior"),
    misconception: val("#misconception"),
    check_for_understanding: val("#cfu"),
    materials: val("#materials"),
    procedure: serializeSteps(stepTexts()),
  });

  // ── persistence (shared by autosave AND the buttons — one lesson, never two) ──
  let lessonId: string | null = null;
  let creating: Promise<string> | null = null;
  let planId: string | null = null;
  let savedFrame = ""; // JSON of the last persisted frame / plan body — skip no-op requests
  let savedPlan = "";
  // True once the teacher has WRITTEN something (a writing box, the topic, the one-line why, an own resource) or
  // pressed a button that needs a real lesson (Save draft, Submit, Make a worksheet). Until then nothing is saved.
  let touched = false;

  /** "Discard this draft" appears once a draft exists. (style, not `hidden`: .btn's display outranks the attribute.) */
  function showDiscard(): void {
    discard.style.display = lessonId ? "" : "none";
    if (lessonId) wireDiscard(lessonId);
  }

  function ensureLesson(keepalive: boolean): Promise<string> {
    if (lessonId) return Promise.resolve(lessonId);
    if (!creating) {
      const f = frame();
      creating = api<{ id: string }>("/api/lessons", {
        method: "POST",
        body: JSON.stringify({ ...f, term_id: term.term.id }),
        keepalive,
      })
        .then((l) => {
          lessonId = l.id;
          savedFrame = JSON.stringify(f);
          showDiscard();
          return l.id;
        })
        .catch((ex) => {
          creating = null; // a failed create may retry on the next tick
          throw ex;
        });
    }
    return creating;
  }

  async function persist(keepalive: boolean): Promise<boolean> {
    // No draft until the teacher writes: opening the form, the prefilled topic and picking a class, week or lesson
    // leave nothing behind (they used to create a lesson and an empty plan, and move the class's position).
    if (!touched && !lessonId && !creating) return false;
    const f = frame();
    if (!f.topic) return false; // nothing findable to save yet — same rule as the buttons
    const id = await ensureLesson(keepalive);
    const fJson = JSON.stringify(f);
    if (fJson !== savedFrame) {
      await api(`/api/lessons/${id}`, { method: "PATCH", body: fJson, keepalive });
      savedFrame = fJson;
    }
    const pJson = JSON.stringify(planBody());
    if (pJson !== savedPlan) {
      const saved = await api<{ plan_id: string }>(`/api/lessons/${id}/plan`, { method: "PUT", body: pJson, keepalive });
      planId = saved.plan_id;
      savedPlan = pJson;
    }
    return true;
  }

  const autosave = initAutosave({ indicator: app.querySelector<HTMLElement>("#autosave")!, save: persist });
  // Autosave is scheduled only by the teacher's own writing. The pickers (class, week, lesson, duration) schedule it
  // only once a draft exists, so a change to the frame reaches it; before that they save nothing.
  const writingBoxes = app.querySelectorAll<HTMLElement>("#topic, #materials, #prior, #misconception, #cfu, textarea.step-box");
  writingBoxes.forEach((el) => el.addEventListener("input", () => { touched = true; autosave.schedule(); }));
  app.querySelectorAll<HTMLElement>("#class, #week, #duration").forEach((el) => {
    el.addEventListener("input", () => { if (lessonId) autosave.schedule(); });
    el.addEventListener("change", () => { if (lessonId) autosave.schedule(); });
  });
  app.querySelector("#week")?.addEventListener("change", followWeek);

  // The materials panel beside the form. Its two writing actions go only where writing is allowed: the book list
  // into the pasteable Materials box, the week's pages into the plan's book-pages list (never a thinking cell or a step).
  ws = mountWorkspace(app, null, {
    classId: () => val("#class") || null,
    lessonId: () => lessonId,
    termId: () => term.term.id,
    weekNo: () => Number(val("#week")),
    prepareWorksheet: async () => {
      if (resources?.hasUnsavedChanges()) throw new Error("Save or cancel the open materials form first.");
      if (!frame().topic) throw new Error("Add a topic first.");
      touched = true; // the worksheet maker needs a real lesson to attach to
      await autosave.settle(); await persist(false);
    },
    onUseSyllabus: (lines) => {
      const box = app.querySelector<HTMLTextAreaElement>("#materials")!;
      const next = appendSourceLines(box.value, lines);
      if (next !== box.value) {
        box.value = next;
        rich.get("materials")?.refresh();
        box.dispatchEvent(new Event("input", { bubbles: true }));
      }
      toast("Sourced starting points are in Objectives / Materials. Your writing is kept.");
    },
    onAddBooks: (lines) => {
      const box = app.querySelector<HTMLTextAreaElement>("#materials");
      if (!box) return;
      const have = box.value.trim();
      const add = lines.filter((l) => !have.includes(l));
      if (add.length) {
        box.value = (have ? have + "\n" : "") + add.map((l) => `- ${l}`).join("\n");
        rich.get("materials")?.refresh();
        box.dispatchEvent(new Event("input", { bubbles: true }));
      }
      toast(add.length ? "Added to Materials — change anything." : "These books are already in Materials.");
    },
    onAddPages: async (b) => { await resources?.addBook(b); },
  });
  resources = mountResources(app, {
    lessonId: () => lessonId,
    ensureLesson: async () => {
      if (!frame().topic) throw new Error(t("plan.topic_missing"));
      touched = true; // an own activity, link, file or book page belongs to a real lesson
      return ensureLesson(false);
    },
    canEdit: true,
  });

  // The map card renders AFTER autosave exists (its inputs bind to the same debounce).
  await switchClass(startClass.id);
  const classSel = app.querySelector("#class") as HTMLSelectElement | null;
  classSel?.addEventListener("change", () => void switchClass(classSel.value));
  const leave = async (): Promise<boolean> => {
    try {
      if (resources?.hasUnsavedChanges()) throw new Error("Save or cancel the open materials form before leaving.");
      await autosave.settle();
      // Nothing was written and no draft exists: leaving creates nothing (no phantom drafts).
      if (!touched && !lessonId) { autosave.dispose(); return true; }
      if (!frame().topic && (["prior", "misconception", "check_for_understanding", "materials"].some((key) => (planBody() as Record<string, string>)[key]?.trim()) || Object.values(stepTexts()).some((text) => text.trim()))) throw new Error("Add a topic before leaving so your writing can be saved.");
      await persist(false);
      autosave.dispose();
      return true;
    } catch (ex) { err.textContent = `Your plan could not be saved. ${(ex as Error).message} Stay here and retry.`; err.hidden = false; return false; }
  };
  const unregisterGuard = registerNavigationGuard(app, leave);
  app.querySelector("#back-week")!.addEventListener("click", () => navigate(planningWeekHref(val("#class"), term.term.id, Number(val("#week")), val("#node"), lessonId)));

  // ── discarding the draft (two taps, no browser dialog — the pattern of the observation page) ──
  // The draft is created on demand, so its id is not known when the page is drawn: the button is wired once it exists.
  function wireDiscard(id: string): void {
    if (discardWired) return;
    discardWired = true;
    wireDraftDelete(discard, id, {
      ask: "Delete it? Tap again",
      before: () => autosave.settle(),
      done: () => { unregisterGuard(); autosave.dispose(); toast("Draft deleted"); navigate("/plans"); },
      fail: (message) => { err.textContent = message; err.hidden = false; },
    });
  }

  async function save(submit: boolean): Promise<void> {
    err.hidden = true;
    if (!frame().topic) {
      err.textContent = t("plan.topic_missing");
      err.hidden = false;
      return;
    }
    // A far jump asks for one explanatory line (±3 is free) — friendly, and only then.
    const d = jumpDistance();
    if (chosenNode() && d > 3 && !frame().node_note) {
      err.textContent = `That lesson is ${d} weeks from this school week's lesson — add one line so your Lead knows why. Then save away.`;
      err.hidden = false;
      (app.querySelector("#node_note") as HTMLInputElement | null)?.focus();
      return;
    }
    // The typed floor (owner ruling 2026-09-03): SUBMIT needs the key activities and knowledge in
    // the teacher's own words — one field at a time, in template order. Drafts are always free.
    if (submit) {
      const body = planBody();
      const short = firstShortfall(
        { prior: body.prior, misconception: body.misconception, check_for_understanding: body.check_for_understanding },
        stepTexts(),
        {
          prior: t("plan.prior"),
          misconception: t("plan.misconception"),
          check_for_understanding: t("plan.cfu"),
        },
      );
      if (short) {
        err.textContent = t(short.kind === "step" ? "plan.floor_step" : "plan.floor_cell", { label: short.label });
        err.hidden = false;
        const id = short.kind === "step" ? `step-${short.field}` : short.field === "check_for_understanding" ? "cfu" : short.field;
        focusField(id);
        return;
      }
    }
    try {
      touched = true; // Save draft and Submit are the teacher asking for a real lesson
      await autosave.settle(); // never race a tick that's already in flight
      await persist(false); // authoritative save of exactly what's on screen now
      if (submit) {
        await api(`/api/lessons/${lessonId}/submit`, { method: "POST" });
      }
      autosave.dispose();
      navigate(`/plan/${planId}`);
    } catch (ex) {
      err.textContent = (ex as Error).message;
      err.hidden = false;
    }
  }

  app.querySelector<HTMLButtonElement>("#draft")!.addEventListener("click", () => void save(false));
  app.querySelector<HTMLButtonElement>("#submit")!.addEventListener("click", () => void save(true));
  if (query.get("stage") === "materials") app.addEventListener("eden:mounted", () => {
    app.querySelector<HTMLElement>("#own-materials")?.scrollIntoView({ block: "start" });
    app.querySelector<HTMLElement>('[data-pr-new="file"]')?.focus({ preventScroll: true });
  }, { once: true });
}
